/**
 * StageNavigation - Handles stage navigation controls and playback
 */

import * as d3 from 'd3';
import { StageNumber } from '../types/stage.types';
import { TOTAL_STAGES, PLAY_INTERVAL_DURATION } from '../constants';
import { AnalyticsService } from '../services/AnalyticsService';

export class StageNavigation {
    private currentStage: StageNumber = 1;
    private isPlaying: boolean = false;
    private playInterval: number | null = null;
    private analytics: AnalyticsService;
    private onStageChange: (stage: StageNumber) => void;

    constructor(
        analytics: AnalyticsService,
        onStageChange: (stage: StageNumber) => void
    ) {
        this.analytics = analytics;
        this.onStageChange = onStageChange;
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        d3.select('#prev-stage').on('click', () => this.changeStage(-1));
        d3.select('#next-stage').on('click', () => this.changeStage(1));
        d3.select('#play-button').on('click', () => this.togglePlay());

        d3.selectAll('.dot').on('click', (event) => {
            const stage = +d3.select(event.target as HTMLElement).attr('data-stage') as StageNumber;
            if (this.isPlaying) {
                this.stopPlay();
            }
            this.setStage(stage);
            this.analytics.logStageNavigation(stage, 'dot_click');
        });
    }

    private changeStage(direction: number): void {
        const newStage = this.currentStage + direction;
        if (newStage >= 1 && newStage <= TOTAL_STAGES) {
            if (this.isPlaying) {
                this.stopPlay();
            }
            this.setStage(newStage as StageNumber);
            this.analytics.logStageNavigation(
                newStage,
                direction > 0 ? 'next' : 'previous'
            );
        }
    }

    setStage(stage: StageNumber): void {
        this.currentStage = stage;
        this.updateUI();
        this.onStageChange(stage);
    }

    private updateUI(): void {
        const stage = this.currentStage;

        

        // Update title
        d3.select('#stage-title').text(
            stage === 5 ? 'Final Results' : `Race ${stage} Results`
        );

        // Update dots
        d3.selectAll('.dot')
            .classed('active', false)
            .filter(function() {
                return +d3.select(this).attr('data-stage') <= stage;
            })
            .classed('active', true);

        // Update buttons
        d3.select('#prev-stage').property('disabled', stage === 1);
        d3.select('#next-stage').property('disabled', stage === TOTAL_STAGES);
    }

    private togglePlay(): void {
        if (this.isPlaying) {
            this.stopPlay();
        } else {
            this.startPlay();
        }
    }

    startPlay(): void {
        this.isPlaying = true;
        this.updatePlayButton();

        // Start from stage 1
        this.setStage(1);

        this.analytics.logAutoPlayStarted();

        // Set up interval to advance stages
        this.playInterval = window.setInterval(() => {
            if (this.currentStage < TOTAL_STAGES) {
                this.setStage((this.currentStage + 1) as StageNumber);
            } else {
                this.stopPlay();
            }
        }, PLAY_INTERVAL_DURATION);
    }

    stopPlay(): void {
        this.isPlaying = false;
        this.updatePlayButton();

        if (this.playInterval) {
            clearInterval(this.playInterval);
            this.playInterval = null;
        }
    }

    private updatePlayButton(): void {
        const playIcon = d3.select('#play-icon');
        const pauseIcon1 = d3.select('#pause-icon');
        const pauseIcon2 = d3.select('#pause-icon-2');

        if (this.isPlaying) {
            playIcon.style('display', 'none');
            pauseIcon1.style('display', 'block');
            pauseIcon2.style('display', 'block');
        } else {
            playIcon.style('display', 'block');
            pauseIcon1.style('display', 'none');
            pauseIcon2.style('display', 'none');
        }
    }

    getCurrentStage(): StageNumber {
        return this.currentStage;
    }

    getIsPlaying(): boolean {
        return this.isPlaying;
    }
}
